import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useSystemVersions } from '@/hooks/useSystemVersions';
import { useTranslation } from '@/hooks/useTranslation';
import { VersionInfo } from '@/types';
import { useState } from 'react';

interface VersionRowProps {
  label: string;
  info: VersionInfo | null;
}

/**
 * Displays version information in a structured grid layout.
 * Shows version, build date, environment, and git commit details.
 * Displays unavailable message if version info is not provided.
 */
const VersionRow = ({ label, info }: VersionRowProps) => {
  const { t, formatDate } = useTranslation();

  if (!info) {
    return (
      <div className='rounded-md border p-3'>
        <div className='mb-2 font-semibold'>{label}</div>
        <p className='text-sm text-muted-foreground'>
          {t('versionInfo.unavailable')}
        </p>
      </div>
    );
  }

  return (
    <div className='rounded-md border p-3'>
      <div className='mb-2 font-semibold'>{label}</div>
      <dl className='grid grid-cols-2 gap-x-2 gap-y-1 text-sm text-muted-foreground'>
        <dt>{t('versionInfo.version')}</dt>
        <dd className='text-right'>{info.version}</dd>
        <dt>{t('versionInfo.buildDate')}</dt>
        <dd className='text-right'>
          {formatDate(info.buildDate, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </dd>
        <dt>{t('versionInfo.environment')}</dt>
        <dd className='text-right'>{info.environment}</dd>
        <dt>{t('versionInfo.commit')}</dt>
        <dd className='text-right'>{info.gitCommit || t('notAvailable')}</dd>
      </dl>
    </div>
  );
};

/** Compact sidebar trigger showing the portal's own version; opens a dialog with all 3 app versions. */
export const SidebarVersionInfo = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { portal, backend, forms, isLoading } = useSystemVersions(open);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Expanded: full "vX.Y.Z" text trigger. */}
      <DialogTrigger asChild>
        <button
          type='button'
          aria-label={t('versionInfo.triggerAriaLabel')}
          className='w-full truncate px-2 py-1 text-left text-xs text-muted-foreground hover:text-foreground group-data-[collapsible=icon]:hidden'
        >
          {t('versionInfo.trigger', { version: portal.version })}
        </button>
      </DialogTrigger>
      {/* Icon rail: compact centered version chip so the version stays
          visible when the sidebar is collapsed. Also opens the dialog. */}
      <DialogTrigger asChild>
        <button
          type='button'
          aria-label={t('versionInfo.triggerAriaLabel')}
          title={t('versionInfo.trigger', { version: portal.version })}
          className='mx-auto hidden max-w-[calc(var(--sidebar-width-icon))] truncate rounded-md px-1 py-0.5 text-center text-[10px] leading-tight text-muted-foreground hover:text-foreground group-data-[collapsible=icon]:block'
        >
          {portal.version}
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('versionInfo.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('versionInfo.dialogDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className='flex flex-col gap-3'>
          <VersionRow label={t('versionInfo.portal')} info={portal} />
          {isLoading ? (
            <p className='text-sm text-muted-foreground'>
              {t('versionInfo.loading')}
            </p>
          ) : (
            <>
              <VersionRow label={t('versionInfo.backend')} info={backend} />
              <VersionRow label={t('versionInfo.forms')} info={forms} />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
